// src/components/ui/Layout/Row.tsx
// Micro layout: Horizontal flex container following IonicLayout.md

import React, { forwardRef } from 'react';
import type { SpacingToken } from './Stack';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export type RowAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type RowJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';

export interface RowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Gap between columns */
  gap?: SpacingToken;
  /** Cross-axis alignment */
  align?: RowAlign;
  /** Main-axis justification */
  justify?: RowJustify;
  /** Allow wrapping to multiple rows */
  wrap?: boolean;
  /** Reverse the row direction */
  reverse?: boolean;
  children: React.ReactNode;
}

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const gapMap: Record<SpacingToken, string> = {
  none: '0',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
};

const alignMap: Record<RowAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
};

const justifyMap: Record<RowJustify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Row = forwardRef<HTMLDivElement, RowProps>(
  (
    {
      gap = 'md',
      align = 'stretch',
      justify = 'start',
      wrap = true,
      reverse = false,
      children,
      className = '',
      style,
      ...rest
    },
    ref
  ) => {
    const computedStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: reverse ? 'row-reverse' : 'row',
      alignItems: alignMap[align],
      justifyContent: justifyMap[justify],
      gap: gapMap[gap],
      flexWrap: wrap ? 'wrap' : 'nowrap',
      width: '100%',
      ...style,
    };

    return (
      <div
        ref={ref}
        style={computedStyle}
        className={className}
        data-layout="Row"
        {...rest}
      >
        {children}
      </div>
    );
  }
);

Row.displayName = 'Row';

export default Row;
